import React from 'react';
import { string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import {
  isTooManyRequestsError,
  isTooManyEmailVerificationRequestsError,
} from '../../../util/errors';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  IconInquiry,
  Heading,
  InlineTextButton,
} from '../../../components';

import css from './InquiryForm.module.css';

const ErrorMessage = props => {
  const {
    error,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = props;
  const [resent, setResent] = React.useState(false);

  if (!error) {
    return null;
  }

  const isEmailNotVerified = error.message === 'email-not-verified';

  const handleResend = () => {
    setResent(false);
    const maybePromise = onResendVerificationEmail && onResendVerificationEmail();
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.then(() => setResent(true));
    }
  };

  return (
    <>
      <p className={css.error}>
        {error.message === 'No transaction process attached to listing' ? (
          <FormattedMessage id="InquiryForm.sendInquiryErrorNoProcess" />
        ) : isEmailNotVerified ? (
          <FormattedMessage id="InquiryForm.emailNotVerifiedError" />
        ) : isTooManyRequestsError(error) ? (
          <FormattedMessage id="InquiryForm.tooManyRequestsError" />
        ) : (
          <FormattedMessage id="InquiryForm.sendInquiryError" />
        )}
      </p>
      {isEmailNotVerified && onResendVerificationEmail ? (
        <p className={css.resendLine}>
          {sendVerificationEmailInProgress ? (
            <FormattedMessage id="InquiryForm.resendingVerificationEmail" />
          ) : sendVerificationEmailError ? (
            <FormattedMessage
              id={
                isTooManyEmailVerificationRequestsError(sendVerificationEmailError)
                  ? 'InquiryForm.resendVerificationFailedTooManyRequests'
                  : 'InquiryForm.resendVerificationFailed'
              }
            />
          ) : resent ? (
            <FormattedMessage id="InquiryForm.resendVerificationSuccess" />
          ) : (
            <InlineTextButton type="button" className={css.resendLink} onClick={handleResend}>
              <FormattedMessage id="InquiryForm.resendVerificationLink" />
            </InlineTextButton>
          )}
        </p>
      ) : null}
    </>
  );
};

// NOTE: this InquiryForm is only for booking & purchase processes
// The default-inquiry process is handled differently
const InquiryFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        intl,
        listingTitle,
        authorDisplayName,
        sendInquiryError,
        onResendVerificationEmail,
        sendVerificationEmailInProgress,
        sendVerificationEmailError,
      } = fieldRenderProps;

      const messageLabel = intl.formatMessage(
        {
          id: 'InquiryForm.messageLabel',
        },
        { authorDisplayName }
      );
      const messagePlaceholder = intl.formatMessage(
        {
          id: 'InquiryForm.messagePlaceholder',
        },
        { authorDisplayName }
      );
      const messageRequiredMessage = intl.formatMessage({
        id: 'InquiryForm.messageRequired',
      });
      const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          <IconInquiry className={css.icon} />
          <Heading as="h2" rootClassName={css.heading}>
            <FormattedMessage id="InquiryForm.heading" values={{ listingTitle }} />
          </Heading>
          <FieldTextInput
            className={css.field}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={messageRequired}
          />
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage
              error={sendInquiryError}
              onResendVerificationEmail={onResendVerificationEmail}
              sendVerificationEmailInProgress={sendVerificationEmailInProgress}
              sendVerificationEmailError={sendVerificationEmailError}
            />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="InquiryForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

InquiryFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  sendInquiryError: null,
  onResendVerificationEmail: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

InquiryFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  sendInquiryError: propTypes.error,
  onResendVerificationEmail: func,
  sendVerificationEmailInProgress: bool,
  sendVerificationEmailError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const InquiryForm = compose(injectIntl)(InquiryFormComponent);

InquiryForm.displayName = 'InquiryForm';

export default InquiryForm;
